type IFirebaseClientCredentials = {
  apiKey: string
  authDomain: string
  projectId: string
  storageBucket: string
  messagingSenderId: string
  appId: string
}

export enum IEnv {
  Local = 'local',
  Dev = 'development',
  Prod = 'production'
}

/**
 * Client-safe config. This object is bundled into the browser build
 * (Next.js only keeps NEXT_PUBLIC_* env vars client-side, and any other
 * `process.env.X` accesses resolve to `undefined` in the browser bundle).
 *
 * DO NOT add secrets here — use IServerConfig / `src/config/server.ts`.
 * If a secret is referenced on this object and someone accidentally calls
 * `JSON.stringify(config)` in a server response, the secret leaks.
 */
type IConfig = {
  env: IEnv
  version: string
  isProd: boolean
  isLocal: boolean
  isServerSide: boolean
  graphqlUrl: string
  baseUrl: string
  serverBaseUrl: string

  logger: {
    apiKey: string
    sourceToken: string
  }

  datadog: {
    clientToken: string
  }

  firebaseClientCredentials: IFirebaseClientCredentials
  googleWebClientId: string
  /** Dedicated Google Static Maps API key (browser-exposed; referrer-restricted). */
  googleMapsStaticKey: string
  recaptcha: {
    siteKey: string
  }
  /**
   * Firebase App Check reCAPTCHA Enterprise site key. Empty string = App Check
   * disabled (the default). See `config/index.ts` for the rollout note.
   */
  firebaseAppCheckSiteKey: string

  pixels: {
    ga: string
    fb: string
    posthog: string
    /** OpenAI / ChatGPT Ads pixel id — see utils/pixels/oaiq.ts */
    openai: string
  }

  hygraph: {
    url: string
  }

  stripe: {
    publishableKey: string
  }
}

/**
 * Server-only secrets. Imported from `src/config/server.ts`.
 * Must only be accessed in API routes, getServerSideProps,
 * getStaticProps, or other server contexts. Never include these
 * values in a response body or client-readable state.
 */
type IServerConfig = {
  clientSecret: string
  cronSecret: string
  datadog: {
    apiKey: string
  }
  recaptcha: {
    secretKey: string
  }
  hygraph: {
    apiKey: string
  }
}

export type { IConfig, IFirebaseClientCredentials, IServerConfig }
